import { uniqueId } from 'lodash';
import { GiftedChat } from 'react-native-gifted-chat';
import apis from '@api/api';
import { SHOW_LOADING_MODAL, HIDE_LOADING_MODAL } from '@reduxLocal/actions/types';

export interface ReportsHandlersConfig {
  dispatch: any;
  globalUser: any;
  setChatMessages: React.Dispatch<React.SetStateAction<any[]>>;
  setSelectedDataSources: (ids: string[]) => void;
  setReportDashboardConfig: (config: any) => void;
}

/**
 * Handle reports keyword detection
 */
export const createHandleReportsKeyword = (config: ReportsHandlersConfig) => {
  const { globalUser, setChatMessages } = config;

  return async () => {
    try {
      console.log('📊 [EasyModeScreen] Reports keyword detected, fetching data sources...');

      const userId = globalUser?._id || globalUser?.id || globalUser?.userId;
      if (!userId) {
        console.warn('⚠️ [EasyModeScreen] No user ID available for reports');
        return;
      }

      const forms = await apis.getDataFolders({ userId: userId, dataFilter: 'all' });
      console.log('📊 [EasyModeScreen] Fetched data sources:', forms?.length || 0);

      if (!forms || forms.length === 0) {
        const noDataMessageId = uniqueId();
        setChatMessages((prevMessages) =>
          GiftedChat.append(prevMessages, [
            {
              _id: noDataMessageId,
              text: "You don't have any data sources yet. Please create or import some data first to build reports.",
              createdAt: new Date(),
              system: true,
              received: true,
            },
          ])
        );
        return;
      }

      const selectorMessageId = uniqueId();
      setChatMessages((prevMessages) =>
        GiftedChat.append(prevMessages, [
          {
            _id: selectorMessageId,
            text: "Select the data sources you'd like to include in your dashboard. You can select multiple sources.",
            createdAt: new Date(),
            system: true,
            showDataSourceSelector: true,
            availableDataSources: forms,
            received: true,
          },
        ])
      );
    } catch (error) {
      console.error('❌ [EasyModeScreen] Error handling reports keyword:', error);
      const errorMessageId = uniqueId();
      setChatMessages((prevMessages) =>
        GiftedChat.append(prevMessages, [
          {
            _id: errorMessageId,
            text: "Sorry, I couldn't load your data sources. Please try again.",
            createdAt: new Date(),
            system: true,
            received: true,
          },
        ])
      );
    }
  };
};

/**
 * Handle data source selection confirmation
 */
export const createHandleDataSourceSelection = (config: ReportsHandlersConfig) => {
  const { dispatch, setChatMessages, setReportDashboardConfig } = config;

  return async (selectedIds: string[]) => {
    // Early return if no IDs selected
    if (selectedIds.length === 0) {
      return;
    }

    try {
      console.log('📊 [EasyModeScreen] Data sources selected:', selectedIds);
      
      // Note: Don't call setSelectedDataSources here - it's already managed by
      // the DataSourceSelector component via onSelectionChange. Calling it here
      // would cause unnecessary re-renders and potential infinite loops.

      dispatch({
        type: SHOW_LOADING_MODAL,
        data: { content: 'Analyzing data sources and suggesting components...' },
      });

      const response = await apis.suggestDashboardComponents(selectedIds);
      dispatch({ type: HIDE_LOADING_MODAL });

      if (response?.success && response?.components) {
        console.log('✅ [EasyModeScreen] Component suggestions received:', response.components.length);

        const dashboardConfig = {
          dataSourceIds: selectedIds,
          components: response.components,
          layout: {},
        };

        setReportDashboardConfig(dashboardConfig);

        const dashboardMessageId = uniqueId();
        setChatMessages((prevMessages) =>
          GiftedChat.append(prevMessages, [
            {
              _id: dashboardMessageId,
              text: "Here's your dashboard preview. You can arrange the components by dragging them. Click 'Save Dashboard' when you're ready.",
              createdAt: new Date(),
              system: true,
              showDashboardBuilder: true,
              dashboardConfig: dashboardConfig,
              received: true,
            },
          ])
        );
      } else {
        throw new Error('Failed to get component suggestions');
      }
    } catch (error) {
      console.error('❌ [EasyModeScreen] Error getting component suggestions:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      const errorMessageId = uniqueId();
      setChatMessages((prevMessages) =>
        GiftedChat.append(prevMessages, [
          {
            _id: errorMessageId,
            text: "Sorry, I couldn't analyze your data sources. Please try again.",
            createdAt: new Date(),
            system: true,
            received: true,
          },
        ])
      );
    }
  };
};

